import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import { Waveform } from "./Waveform.js";
function formatTime(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m.toString().padStart(2, "0")}:${r.toString().padStart(2, "0")}`;
}
const PILL_HEIGHT = 56;
const FOOTER_HEIGHT = 88;
const DOT_COLOR_BY_TONE = {
  live: "var(--voice-status-live-dot, #10B981)",
  connecting: "var(--voice-status-connecting-dot, #A7C7E7)",
  error: "var(--voice-status-error-dot, #EF4444)",
  muted: "var(--voice-status-ended-dot, #9CA3AF)",
  idle: "var(--voice-status-ended-dot, #9CA3AF)"
};
const ControlBar = ({
  muted,
  onToggleMute,
  onEnd,
  seconds,
  active,
  disabled,
  statusLabel = "",
  statusTone = "live"
}) => {
  const waveformColor = muted ? "var(--voice-header-fg, #9CA3AF)" : "var(--voice-header-fg, #1F2937)";
  const showStatus = statusLabel.length > 0;
  return /* @__PURE__ */ jsxs(
    "footer",
    {
      style: {
        position: "relative",
        zIndex: 3,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: FOOTER_HEIGHT,
        padding: "0 16px",
        background: "transparent"
      },
      children: [
        /* @__PURE__ */ jsx("style", { children: PILL_CSS }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "voice-pill",
            style: {
              width: "100%",
              maxWidth: 500,
              height: PILL_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "0 10px 0 14px",
              background: "var(--voice-bar-bg, #FFFFFF)",
              borderRadius: 9999,
              boxShadow: "0 8px 24px -10px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.08)"
            },
            children: [
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  onClick: onToggleMute,
                  disabled,
                  "aria-pressed": muted,
                  "aria-label": muted ? "Unmute microphone" : "Mute microphone",
                  className: "voice-pill-mic",
                  style: {
                    appearance: "none",
                    border: "none",
                    background: muted ? "var(--voice-mute-bg-active, #FEE2E2)" : "transparent",
                    color: muted ? "var(--voice-status-error-dot, #DC2626)" : "var(--voice-header-fg, #1F2937)",
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    transition: "background 160ms ease, color 160ms ease"
                  },
                  children: muted ? /* @__PURE__ */ jsx(MicMutedIcon, {}) : /* @__PURE__ */ jsx(MicIcon, {})
                }
              ),
              /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    flex: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    minWidth: 0
                  },
                  "aria-live": "polite",
                  "aria-atomic": "true",
                  children: showStatus ? /* @__PURE__ */ jsx(FooterStatus, { label: statusLabel, tone: statusTone }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                    /* @__PURE__ */ jsx(Waveform, { active: active && !muted, color: waveformColor, mirror: true }),
                    /* @__PURE__ */ jsx(
                      "span",
                      {
                        style: {
                          fontVariantNumeric: "tabular-nums",
                          fontWeight: 600,
                          fontSize: 14,
                          color: "var(--voice-header-fg, #1F2937)",
                          minWidth: 44,
                          textAlign: "center",
                          letterSpacing: 0.4
                        },
                        children: formatTime(seconds)
                      }
                    ),
                    /* @__PURE__ */ jsx(Waveform, { active: active && !muted, color: waveformColor })
                  ] })
                }
              ),
              /* @__PURE__ */ jsxs(
                "button",
                {
                  type: "button",
                  onClick: onEnd,
                  disabled,
                  "aria-label": "End call",
                  className: "voice-pill-end",
                  style: {
                    appearance: "none",
                    border: "none",
                    background: "var(--voice-end-bg, #DC2626)",
                    borderRadius: 9999,
                    height: 40,
                    padding: "0 14px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.6 : 1,
                    transition: "transform 80ms ease, opacity 120ms ease"
                  },
                  children: [
                    /* @__PURE__ */ jsx(CloseIcon, {}),
                    /* @__PURE__ */ jsx("span", { className: "voice-pill-end-label", style: { color: "#FFFFFF" }, children: "End" })
                  ]
                }
              )
            ]
          }
        )
      ]
    }
  );
};
const FooterStatus = ({ label, tone }) => {
  const [current, setCurrent] = useState(label);
  const [previous, setPrevious] = useState(null);
  const fadeTimer = useRef(null);
  const dotColor = DOT_COLOR_BY_TONE[tone];
  useEffect(() => {
    if (label === current) return;
    setPrevious(current);
    setCurrent(label);
    if (fadeTimer.current != null) window.clearTimeout(fadeTimer.current);
    fadeTimer.current = window.setTimeout(() => setPrevious(null), 200);
    return () => {
      if (fadeTimer.current != null) {
        window.clearTimeout(fadeTimer.current);
        fadeTimer.current = null;
      }
    };
  }, [label, current]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        position: "relative",
        minHeight: 20,
        maxWidth: "100%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center"
      },
      children: [
        previous && /* @__PURE__ */ jsxs(
          "span",
          {
            style: {
              ...statusLabelStyle,
              position: "absolute",
              inset: 0,
              opacity: 0,
              justifyContent: "center"
            },
            children: [
              /* @__PURE__ */ jsx(StatusDot, { color: dotColor }),
              /* @__PURE__ */ jsx("span", { style: statusTextStyle, children: previous })
            ]
          }
        ),
        current && /* @__PURE__ */ jsxs("span", { style: { ...statusLabelStyle, justifyContent: "center" }, children: [
          /* @__PURE__ */ jsx(StatusDot, { color: dotColor, animate: true }),
          /* @__PURE__ */ jsx("span", { style: statusTextStyle, children: current })
        ] })
      ]
    }
  );
};
const statusLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  maxWidth: "100%",
  transition: "opacity 200ms ease"
};
const statusTextStyle = {
  fontSize: 14,
  fontWeight: 400,
  letterSpacing: 0.2,
  color: "var(--voice-header-fg, #1F2937)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};
const StatusDot = ({ color, animate = false }) => /* @__PURE__ */ jsx(
  "span",
  {
    "aria-hidden": true,
    style: {
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: color,
      boxShadow: `0 0 6px ${color}`,
      animation: animate ? "voice-footer-status-pulse 1.4s ease-in-out infinite" : "none",
      flexShrink: 0
    }
  }
);
const PILL_CSS = `
.voice-pill-mic:not(:disabled):hover { background: var(--voice-mute-hover-bg, #F3F4F6); }
.voice-pill-end:not(:disabled):hover { transform: translateY(-1px); }

.voice-pill-end,
.voice-pill-end .voice-pill-end-label {
  color: #FFFFFF !important;
}
.voice-pill-end svg path {
  stroke: #FFFFFF !important;
}

@keyframes voice-footer-status-pulse {
  0%, 100% { opacity: 0.45; }
  50%      { opacity: 1; }
}

@media (max-width: 759.98px) {
  .voice-pill-end {
    width: 44px;
    height: 44px;
    padding: 0 !important;
    justify-content: center;
  }
  .voice-pill-end-label { display: none; }
}
`;
const MicIcon = () => /* @__PURE__ */ jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Z",
      stroke: "currentColor",
      strokeWidth: "1.7",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M6 11a6 6 0 0 0 12 0M12 17v3",
      stroke: "currentColor",
      strokeWidth: "1.7",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  )
] });
const MicMutedIcon = () => /* @__PURE__ */ jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M9 9.5V6a3 3 0 0 1 5.83-1M15 11.4V11M12 17v3M6 11a6 6 0 0 0 9.5 4.9",
      stroke: "currentColor",
      strokeWidth: "1.7",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ jsx("path", { d: "M4 4l16 16", stroke: "currentColor", strokeWidth: "1.7", strokeLinecap: "round" })
] });
const CloseIcon = () => /* @__PURE__ */ jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M6 6l12 12M18 6L6 18",
    stroke: "#FFFFFF",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
export {
  ControlBar
};
//# sourceMappingURL=ControlBar.js.map
